import { create } from "zustand";
import {
  buildWindows,
  loadDeviceHistory,
  processInBatches,
  DEVICE_CONCURRENCY,
} from "../../pages/reports/utils/trackingHistoryWindows";
import { analyzeQuality, type QualitySummary } from "../../pages/reports/utils/gpsQuality";

export interface GpsQualityResult {
  deviceId: string;
  vehicleName: string;
  summary: QualitySummary | null;
  loading: boolean;
  error: string | null;
}

interface ReportGpsQualityState {
  results: Record<string, GpsQualityResult>;
  order: string[];
  loading: boolean;
  tz: string | null;
  loadRange: (devices: { deviceId: string; vehicleName: string }[], from: string, to: string, tz?: string) => Promise<void>;
  clear: () => void;
}

export const useReportGpsQualityStore = create<ReportGpsQualityState>()((set) => ({
  results: {},
  order: [],
  loading: false,
  tz: null,

  loadRange: async (devices, from, to, tz) => {
    const resolvedTz = tz || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const windows = buildWindows(new Date(from).getTime(), new Date(to).getTime());
    const initial: Record<string, GpsQualityResult> = {};
    devices.forEach((d) => {
      initial[d.deviceId] = { ...d, summary: null, loading: true, error: null };
    });
    set({ results: initial, order: devices.map((d) => d.deviceId), loading: true, tz: resolvedTz });

    await processInBatches(devices, DEVICE_CONCURRENCY, async (d) => {
      const { points, failedChunks } = await loadDeviceHistory(d.deviceId, windows, resolvedTz);
      const summary = analyzeQuality(points);
      set((state) => {
        const current = state.results[d.deviceId];
        if (!current) return state;
        return {
          results: {
            ...state.results,
            [d.deviceId]: {
              ...current,
              summary,
              loading: false,
              error:
                points.length === 0 && failedChunks > 0
                  ? "No se pudo leer el historial (servicio no disponible)."
                  : failedChunks > 0
                    ? `${failedChunks} tramo(s) con error: el análisis puede estar incompleto`
                    : null,
            },
          },
        };
      });
    });

    set({ loading: false });
  },

  clear: () => set({ results: {}, order: [], loading: false, tz: null }),
}));
